import React, { useMemo, useState, useEffect, useRef } from "react";
import { Button, Card, Row, Col, Typography, Space, Input, DatePicker, Spin, Tag, Table, message as antMessage } from "antd";
import { UploadOutlined, DeleteOutlined, FileExcelOutlined } from "@ant-design/icons";
import Select from "react-select";
import { PhoneOutlined, LinkOutlined, CopyOutlined, CommentOutlined, TeamOutlined, GlobalOutlined, BankOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchApprovedTemplates } from "../../../core/data/redux/slices/ApprovedTemplatesSlice";
import api from "../../../core/axios/axiosInstance";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;

const TARGET_OPTIONS = [
  {
    value: "all",
    label: "Everyone",
    description: "All company admins + all agents",
    icon: <GlobalOutlined style={{ fontSize: 18, color: "#6366f1" }} />,
    color: "#ede9fe", border: "#6366f1",
  },
  {
    value: "companies",
    label: "Company Admins",
    description: "Only company admin accounts",
    icon: <BankOutlined style={{ fontSize: 18, color: "#0ea5e9" }} />,
    color: "#e0f2fe", border: "#0ea5e9",
  },
  {
    value: "specific",
    label: "Specific Companies",
    description: "Select which companies to notify",
    icon: <TeamOutlined style={{ fontSize: 18, color: "#f59e0b" }} />,
    color: "#fef3c7", border: "#f59e0b",
  },
  {
    value: "excel",
    label: "Excel Upload",
    description: "Upload a list of custom numbers",
    icon: <FileExcelOutlined style={{ fontSize: 18, color: "#1d6f42" }} />,
    color: "#dcfce7", border: "#16a34a",
  },
];

export default function AdminWhatsappAddCampaign() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: approvedTemplatesData, loading: templatesLoading } = useSelector(s => s.approvedTemplates);

  const [campaignName, setCampaignName] = useState("");
  const [target, setTarget] = useState("all");
  const [selectedTemplateOption, setSelectedTemplateOption] = useState(null);
  const [scheduleTime, setScheduleTime] = useState(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [buttonConfig, setButtonConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [excelContacts, setExcelContacts] = useState([]);
  const [excelFileName, setExcelFileName] = useState("");
  const fileInputRef = useRef(null);

  // Specific company multi-select
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  useEffect(() => { dispatch(fetchApprovedTemplates()); }, [dispatch]);

  useEffect(() => {
    if (target !== "specific" || companies.length > 0) return;
    setCompaniesLoading(true);
    api.post("/superAdmin/allCompanyAdmin", { limit: 1000 })
      .then(res => {
        const list = res.data?.data || [];
        setCompanies(list.map(c => {
          const companyName = c.userInfo?.companyName;
          const ownerName = [c.firstname, c.lastname].filter(Boolean).join(" ");
          const label = companyName ? `${companyName}${ownerName ? ` (${ownerName})` : ""}` : ownerName || c.email;
          return { value: c._id, label };
        }));
      })
      .catch(() => setCompanies([]))
      .finally(() => setCompaniesLoading(false));
  }, [target]);

  const approvedTemplates = useMemo(() =>
    approvedTemplatesData?.map(t => ({ label: t.name, value: t.metaTemplateId, template: t })) || [],
    [approvedTemplatesData]
  );

  const selectedTemplate = selectedTemplateOption?.template;
  const headerComponent = selectedTemplate?.components?.find(c => c.type === "HEADER");
  const bodyComponent = selectedTemplate?.components?.find(c => c.type === "BODY");
  const footerComponent = selectedTemplate?.components?.find(c => c.type === "FOOTER");
  const buttonComponent = selectedTemplate?.components?.find(c => c.type === "BUTTONS");
  const headerFormat = headerComponent?.format?.toLowerCase();
  const headerMediaUrl = headerComponent?.media?.s3Url;

  const getBtnIcon = (type) => {
    if (type === "PHONE_NUMBER") return <PhoneOutlined />;
    if (type === "URL") return <LinkOutlined />;
    if (type === "COPY_CODE") return <CopyOutlined />;
    return <CommentOutlined />;
  };

  const renderHeader = () => {
    if (!headerComponent) return null;
    if (headerFormat === "text" && headerComponent.text) return <div style={{ fontWeight: 700, marginBottom: 8 }}>{headerComponent.text}</div>;
    if (headerFormat === "image" && headerMediaUrl) return <img src={headerMediaUrl} alt="header" style={{ width: "100%", borderRadius: 8 }} />;
    if (headerFormat === "video" && headerMediaUrl) return <video src={headerMediaUrl} controls style={{ width: "100%", borderRadius: 8 }} />;
    if (headerFormat === "document") return <div style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>📄 {headerComponent.media?.fileName || "Document"}</div>;
    return null;
  };

  const buildParams = () => {
    const params = {};
    if (buttonComponent?.buttons?.length) {
      const buttons = buttonComponent.buttons.map((btn, i) => {
        const cfg = buttonConfig[i];
        if (!cfg?.value) return null;
        return cfg.value;
      });
      if (buttons.some(Boolean)) params.buttons = buttons;
    }
    return params;
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!rows.length) { antMessage.error("Excel file is empty"); return; }
        const keys = Object.keys(rows[0]);
        const nameKey = keys.find(k => /name/i.test(k)) || keys[0];
        const numberKey = keys.find(k => /number|phone|mobile|tel/i.test(k)) || keys[1];
        const parsed = rows
          .map(row => ({
            name: String(row[nameKey] || "").trim(),
            number: String(row[numberKey] || "").replace(/[^0-9]/g, ""),
          }))
          .filter(r => r.number.length >= 7);
        if (!parsed.length) { antMessage.error("No valid phone numbers found in the file"); return; }
        setExcelContacts(parsed);
        setExcelFileName(file.name);
        antMessage.success(`${parsed.length} contacts loaded from "${file.name}"`);
      } catch {
        antMessage.error("Failed to parse Excel file");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!campaignName.trim()) return alert("Campaign name is required");
    if (!selectedTemplateOption) return alert("Please select a template");
    if (target === "specific" && !selectedCompanies.length) return alert("Please select at least one company");
    if (target === "excel" && !excelContacts.length) return alert("Please upload an Excel file with contacts");

    const scheduleUTC = scheduleEnabled && scheduleTime
      ? dayjs(scheduleTime).tz("Asia/Dubai", true).utc().toISOString()
      : undefined;

    const payload = {
      campaignName: campaignName.trim(),
      templateId: selectedTemplateOption.template._id,
      target: target === "excel" ? "excel" : target,
      ...(target === "specific" && { targetCompanyIds: selectedCompanies.map(c => c.value) }),
      ...(target === "excel" && { excelNumbers: excelContacts }),
      ...(scheduleUTC && { schedule: scheduleUTC }),
      params: buildParams(),
    };

    setSaving(true);
    try {
      console.log(payload,"payload from superadmin campaign");
      
     const response = await api.post("/superAdmin/whatsapp/send-campaign", payload);
     console.log(response.data,"response from the superadmin campaign");
     
      navigate(-1);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card mt-3">
          <div className="card-body pb-2">
            <h4 className="page-title mb-0">
              <span onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
                <i className="ti ti-arrow-narrow-left me-3" />
              </span>
              Create WhatsApp Campaign
            </h4>
          </div>
        </div>

        <Row gutter={24}>
          {/* LEFT FORM */}
          <Col span={14}>
            <Card>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>

                <div>
                  <Text strong>Campaign Name</Text>
                  <Input
                    size="large"
                    placeholder="Enter campaign name"
                    value={campaignName}
                    onChange={e => setCampaignName(e.target.value)}
                  />
                </div>

                {/* Audience selector */}
                <div>
                  <Text strong>Send To</Text>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                    {TARGET_OPTIONS.map(opt => (
                      <div
                        key={opt.value}
                        onClick={() => { setTarget(opt.value); setSelectedCompanies([]); }}
                        style={{
                          flex: "1 1 140px",
                          border: `2px solid ${target === opt.value ? opt.border : "#e5e7eb"}`,
                          borderRadius: 10, padding: "10px 12px", cursor: "pointer",
                          background: target === opt.value ? opt.color : "#fff",
                          transition: "all 0.15s",
                          display: "flex", alignItems: "flex-start", gap: 8,
                        }}
                      >
                        {opt.icon}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "#111827" }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.3 }}>{opt.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {target === "specific" && (
                    <div style={{ marginTop: 10 }}>
                      {companiesLoading ? <Spin size="small" /> : (
                        <Select
                          isMulti
                          options={companies}
                          value={selectedCompanies}
                          onChange={setSelectedCompanies}
                          placeholder="Search and select companies…"
                        />
                      )}
                    </div>
                  )}

                  {target === "excel" && (
                    <div style={{ marginTop: 10 }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        onChange={handleExcelUpload}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Button
                          icon={<UploadOutlined />}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {excelFileName || "Choose File (.xlsx / .xls)"}
                        </Button>
                        {excelContacts.length > 0 && (
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={() => { setExcelContacts([]); setExcelFileName(""); }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      {excelContacts.length > 0 ? (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ marginBottom: 6, fontSize: 13, color: "#555" }}>
                            <FileExcelOutlined style={{ color: "#1d6f42", marginRight: 6 }} />
                            <strong>{excelContacts.length}</strong> contacts loaded
                          </div>
                          <Table
                            size="small"
                            dataSource={excelContacts.slice(0, 5).map((r, i) => ({ ...r, key: i }))}
                            columns={[
                              { title: "Name", dataIndex: "name", key: "name", render: v => v || <span style={{ color: "#bbb" }}>—</span> },
                              { title: "Number", dataIndex: "number", key: "number" },
                            ]}
                            pagination={false}
                            footer={() => excelContacts.length > 5 ? `...and ${excelContacts.length - 5} more` : null}
                            style={{ border: "1px solid #f0f0f0", borderRadius: 6 }}
                          />
                        </div>
                      ) : (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                          Excel must have columns: <Tag>Name</Tag> <Tag>Number</Tag>. Numbers should include country code (e.g. 971501234567).
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Row gutter={16}>
                  <Col span={24}>
                    <Text strong>Template</Text>
                    <Select
                      isLoading={templatesLoading}
                      options={approvedTemplates}
                      value={selectedTemplateOption}
                      onChange={v => { setSelectedTemplateOption(v); setButtonConfig({}); }}
                      placeholder="Select approved template"
                    />
                  </Col>
                </Row>

                <div>
                  <Text strong>Schedule</Text>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 6 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={scheduleEnabled} onChange={e => setScheduleEnabled(e.target.checked)} />
                      Enable scheduling
                    </label>
                    {scheduleEnabled && (
                      <DatePicker
                        showTime={{ use12Hours: true, format: "hh:mm A" }}
                        format="YYYY-MM-DD hh:mm A"
                        style={{ flex: 1 }}
                        onChange={setScheduleTime}
                        disabledDate={d => d && d < dayjs().startOf("day")}
                      />
                    )}
                  </div>
                  {!scheduleEnabled && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Campaign will be sent immediately.</div>}
                </div>

                {buttonComponent?.buttons?.length > 0 && (
                  <Card size="small" title="Button Configuration">
                    {buttonComponent.buttons.map((btn, i) => (
                      <div key={i} style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          {getBtnIcon(btn.type)} Button {i + 1} ({btn.type})
                        </div>
                        <Select
                          className="mb-2"
                          options={[{ label: "Static", value: "static" }, { label: "Dynamic", value: "dynamic" }]}
                          onChange={opt => setButtonConfig(prev => ({ ...prev, [i]: { type: opt.value } }))}
                          placeholder="Select type"
                        />
                        {buttonConfig[i]?.type === "static" && (
                          <Input placeholder="Enter value" onChange={e => setButtonConfig(prev => ({ ...prev, [i]: { ...prev[i], value: e.target.value } }))} />
                        )}
                      </div>
                    ))}
                  </Card>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <Button size="large" onClick={() => navigate(-1)}>Cancel</Button>
                  <Button
                    type="primary" size="large" loading={saving}
                    style={{ background: "#6366f1", borderColor: "#6366f1" }}
                    onClick={handleSave}
                  >
                    {scheduleEnabled ? "Schedule Campaign" : "Send Campaign"}
                  </Button>
                </div>
              </Space>
            </Card>
          </Col>

          {/* RIGHT PREVIEW */}
          <Col span={10}>
            <Card
              style={{ backgroundImage: "url('/assets/img/wabaBg.png')", backgroundRepeat: "repeat", minHeight: 400 }}
            >
              <Title level={5}>Template Preview</Title>
              {selectedTemplate ? (
                <div style={{ background: "#fff", borderRadius: 12, padding: 14 }}>
                  {renderHeader()}
                  {bodyComponent?.text && <div style={{ marginBottom: 8 }}>{bodyComponent.text}</div>}
                  {footerComponent?.text && <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>{footerComponent.text}</div>}
                  {buttonComponent?.buttons?.map((btn, i) => (
                    <Button key={i} block style={{ marginBottom: 6 }} icon={getBtnIcon(btn.type)}>{btn.text}</Button>
                  ))}
                </div>
              ) : (
                <Text type="secondary">Select a template to preview it here.</Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
